"use client";

import { useAuth } from "@/contexts/AuthContext";
import { useKYCDocuments } from "./_components/useKYCDocuments";
import KYCHeader from "./_components/KYCHeader";
import DocumentUploadCards from "./_components/DocumentUploadCards";
import AddDocumentForm from "./_components/AddDocumentForm";
import EditDocumentForm from "./_components/EditDocumentForm";
import DeleteDocumentModal from "./_components/DeleteDocumentModal";
import RoleRequirementNotice from "./_components/RoleRequirementNotice";
import { normalizeKycStatus } from "./_components/constants";

interface KYCPageDevOrchestratorProps {
  onClose?: () => void;
}

export function KYCPageDevOrchestrator({
  onClose,
}: KYCPageDevOrchestratorProps) {
  const kyc = useKYCDocuments({ onAllDocumentsUploaded: onClose });
  const { user, isRealtor, isDeveloper } = useAuth();

  const kycStatus = normalizeKycStatus(user?.kyc_status);
  const canShowUploadForms = !kyc.isVerified;

  return (
    <section
      aria-label="KYC verification"
      className="max-w-4xl mx-auto space-y-6 text-slate-700 font-sans p-2"
    >
      <KYCHeader kycStatus={kycStatus} onClose={onClose} />

      {!kyc.isVerified && (
        <RoleRequirementNotice
          isRealtor={isRealtor}
          isDeveloper={isDeveloper}
        />
      )}

      {!kyc.isAddFormOpen && !kyc.editingDocument && (
        <DocumentUploadCards
          documents={kyc.documents}
          stagedDocuments={kyc.stagedDocuments}
          isVerified={kyc.isVerified}
          isRealtor={isRealtor}
          isDeveloper={isDeveloper}
          onUploadType={kyc.startAddDocument}
          onEdit={kyc.startEdit}
          onDelete={kyc.setDocumentToDelete}
          onRemoveStaged={kyc.removeStagedDocument}
          onSubmit={() => kyc.submitMutation.mutate()}
          isSubmitPending={kyc.submitMutation.isPending}
        />
      )}

      {kyc.editingDocument && canShowUploadForms && (
        <EditDocumentForm
          documentTypeLabel={kyc.editingDocument.type.toUpperCase()}
          editNumber={kyc.editNumber}
          onEditNumberChange={kyc.handleEditNumberChange}
          firstName={kyc.editFirstName}
          onFirstNameChange={kyc.setEditFirstName}
          lastName={kyc.editLastName}
          onLastNameChange={kyc.setEditLastName}
          file={kyc.editFile}
          isUploading={kyc.isEditUploading}
          onFileChange={(file) => kyc.handleFile(file, true)}
          onRemoveFile={kyc.removeEditFile}
          isPending={kyc.editMutation.isPending}
          onCancel={kyc.cancelEdit}
          onSubmit={() => kyc.editMutation.mutate()}
        />
      )}

      {kyc.isAddFormOpen && canShowUploadForms && (
        <AddDocumentForm
          documentType={kyc.documentType}
          documentNumber={kyc.documentNumber}
          onDocumentNumberChange={kyc.handleDocumentNumberChange}
          firstName={kyc.firstName}
          onFirstNameChange={kyc.setFirstName}
          lastName={kyc.lastName}
          onLastNameChange={kyc.setLastName}
          file={kyc.file}
          isUploading={kyc.isUploading}
          uploadedFileUrl={kyc.uploadedFileUrl}
          onFileChange={(file) => kyc.handleFile(file, false)}
          onRemoveFile={kyc.removeFile}
          isPending={false}
          onCancel={kyc.cancelAddDocument}
          onSubmit={kyc.stageDocument}
        />
      )}

      <DeleteDocumentModal
        document={kyc.documentToDelete}
        isPending={kyc.deleteMutation.isPending}
        onConfirm={() =>
          kyc.documentToDelete &&
          kyc.deleteMutation.mutate(kyc.documentToDelete.id)
        }
        onClose={() => kyc.setDocumentToDelete(null)}
      />
    </section>
  );
}

export default KYCPageDevOrchestrator;
